/**
 * Schedule Selector Component
 *
 * Dropdown for selecting a Calendly schedule
 */

import { useState, useRef, useEffect } from 'react'
import { Calendar, ChevronDown, Clock, Check, Loader2 } from 'lucide-react'
import {
  useCalendlySchedules,
  useDefaultSchedule,
  type CalendlySchedule,
} from '@/lib/integrations/calendly'

export interface ScheduleSelectorProps {
  value?: string
  onChange: (scheduleId: string) => void
  placeholder?: string
  disabled?: boolean
}

export function ScheduleSelector({
  value,
  onChange,
  placeholder = 'Select a schedule',
  disabled = false,
}: ScheduleSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data: schedules, isLoading: isLoadingSchedules } = useCalendlySchedules()
  const { data: defaultSchedule } = useDefaultSchedule()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Auto-select default schedule if no value is set
  useEffect(() => {
    if (!value && defaultSchedule && !disabled) {
      onChange(defaultSchedule.id)
    }
  }, [value, defaultSchedule, onChange, disabled])

  const selectedSchedule = schedules?.find((s) => s.id === value)

  const handleSelect = (schedule: CalendlySchedule) => {
    onChange(schedule.id)
    setIsOpen(false)
  }

  if (isLoadingSchedules) {
    return (
      <div className="flex items-center gap-2 py-2.5 px-3 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
        <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
        <span className="text-sm text-zinc-400">Loading schedules...</span>
      </div>
    )
  }

  if (!schedules || schedules.length === 0) {
    return (
      <div className="flex items-center gap-2 py-2.5 px-3 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
        <Calendar className="w-4 h-4 text-zinc-400" />
        <span className="text-sm text-zinc-500 dark:text-zinc-400">
          No schedules available. Connect Calendly first.
        </span>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-lg border transition-colors text-left
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isOpen ? 'border-[#FF6F00] ring-1 ring-[#FF6F00]' : 'border-zinc-200 dark:border-zinc-800'}
          bg-white dark:bg-zinc-900
        `}
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedSchedule ? (
            <>
              {selectedSchedule.color && (
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: selectedSchedule.color }}
                />
              )}
              <span className="text-sm text-zinc-900 dark:text-zinc-100 truncate">
                {selectedSchedule.name}
              </span>
              <span className="flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
                <Clock className="w-3 h-3" />
                {selectedSchedule.duration_minutes}m
              </span>
            </>
          ) : (
            <>
              <Calendar className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-zinc-500 dark:text-zinc-400">{placeholder}</span>
            </>
          )}
        </div>

        <ChevronDown
          className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 py-1 bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 shadow-lg max-h-64 overflow-auto">
          {schedules.map((schedule) => (
            <button
              key={schedule.id}
              onClick={() => handleSelect(schedule)}
              className={`
                w-full flex items-center gap-2 px-3 py-2 text-left transition-colors
                ${
                  schedule.id === value
                    ? 'bg-[#FF6F00]/10 text-[#FF6F00]'
                    : 'hover:bg-zinc-50 dark:hover:bg-zinc-800'
                }
              `}
            >
              {schedule.color && (
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: schedule.color }}
                />
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm truncate ${
                      schedule.id === value
                        ? 'text-[#FF6F00]'
                        : 'text-zinc-900 dark:text-zinc-100'
                    }`}
                  >
                    {schedule.name}
                  </span>
                  {schedule.is_default && (
                    <span className="text-xs text-zinc-400">(default)</span>
                  )}
                </div>
                <span className="flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
                  <Clock className="w-3 h-3" />
                  {schedule.duration_minutes} minutes
                </span>
              </div>

              {schedule.id === value && <Check className="w-4 h-4 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
